import { useEffect, useRef, useState } from 'react';
import {
  forestIssues,
  forestSources,
  forestCases,
  type ForestSourceGroup,
  findForestIssue,
  type ResearchCapability,
  type ResearchIssue,
} from './forest-research-content';
import type { DetailLocation } from './ScenarioDetailView';
import { ScenarioCardArt } from './ScenarioCardArt';
import { ForestResearchImage } from './ForestResearchImage';
import { ResearchInformationDrawer } from './ResearchInformationDrawer';
import type {
  ResearchDrawerCase,
  ResearchDrawerSource,
  ResearchInformationDetail,
} from './research-information';
import researchIndex from './assets/forest/forest-research-index.pdf?url';
import {
  ScenarioCapabilityReadingCard,
  ScenarioCapabilityReadingDrawer,
} from './ScenarioCapabilityReading';
import { forestCapabilityReading } from './scenario-capability-editorial';
import { ForestExtendedTechnicalIllustrations } from './ForestExtendedTechnicalIllustrations';
import { handleScenarioTabsKeyDown } from './scenario-tab-keyboard';
import { platformRoutes } from '../../../shared/platform/platform-routes';

/** Same two-level reading layout; never fabricate a source-bound ScenarioProjection. */
export function ForestScenarioDetailView({
  location,
  onLocation,
  onLibrary,
}: {
  readonly location: DetailLocation;
  readonly onLocation: (next: DetailLocation) => void;
  readonly onLibrary: () => void;
}) {
  const issue = findForestIssue(location.issue) ?? forestIssues[0];
  const [drawer, setDrawer] = useState<ResearchCapability | null>(null);
  const [infoNeed, setInfoNeed] = useState<ResearchIssue['information'][number] | null>(null);
  const [all, setAll] = useState(false);
  const [group, setGroup] = useState<ForestSourceGroup | null>(null);
  const readingScroll = useRef(0);
  const previousTab = useRef(location.tab);
  const opener = useRef<HTMLElement | null>(null);
  const infoOpener = useRef<HTMLElement | null>(null);
  const information = useRef<HTMLElement>(null);
  const capabilities = useRef<HTMLElement>(null);
  useEffect(() => {
    setDrawer(null);
    setInfoNeed(null);
    setAll(false);
    setGroup(null);
  }, [location.issue, location.tab]);
  useEffect(() => {
    if (location.tab !== 'issues' || !location.focus) return;
    (location.focus === 'information' ? information : capabilities).current?.scrollIntoView({
      block: 'start',
    });
  }, [location.issue, location.tab, location.focus]);
  useEffect(() => {
    if (previousTab.current === 'evidence' && location.tab === 'issues')
      window.scrollTo(0, readingScroll.current);
    if (previousTab.current === 'issues' && location.tab === 'evidence') window.scrollTo(0, 0);
    previousTab.current = location.tab;
  }, [location.tab]);
  if (!issue) return null;
  const changeTab = (tab: 'issues' | 'evidence') => {
    if (location.tab === 'issues' && tab === 'evidence') readingScroll.current = window.scrollY;
    onLocation({ ...location, tab, focus: null });
  };
  const toEvidence = () => changeTab('evidence');
  return (
    <section className="scc-detail" data-reference-nature="PUBLIC_RESEARCH_REFERENCE">
      <nav className="scc-breadcrumb" aria-label="面包屑">
        <button onClick={onLibrary}>场景中心</button>
        <span aria-hidden="true">›</span>
        <strong>森林火灾</strong>
      </nav>
      <header className="scc-scene-head">
        <div className="scc-scene-art">
          <ScenarioCardArt id="forest-fire" name="森林火灾" />
        </div>
        <div className="scc-scene-title">
          <h1>森林火灾</h1>
          <p>常态监测、早期线索、环境与人员安全。</p>
        </div>
        <button className="scc-scene-back" onClick={onLibrary}>
          返回场景库 →
        </button>
      </header>
      <div
        className="scc-tab-bar"
        role="tablist"
        aria-label="场景详情"
        onKeyDown={handleScenarioTabsKeyDown}
      >
        {(['issues', 'evidence'] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            id={'scc-forest-tab-' + tab}
            aria-selected={location.tab === tab}
            aria-controls="scc-forest-panel"
            tabIndex={location.tab === tab ? 0 : -1}
            onClick={() => changeTab(tab)}
          >
            {tab === 'issues' ? '典型问题' : '依据与案例'}
          </button>
        ))}
      </div>
      <div id="scc-forest-panel" role="tabpanel" aria-labelledby={'scc-forest-tab-' + location.tab}>
        {location.tab === 'issues' ? (
          <div className="scc-issue-layout">
            <aside className="scc-issue-list" aria-label="典型问题">
              <h2>典型问题</h2>
              <ul>
                {forestIssues.map((row) => (
                  <li key={row.key}>
                    <button
                      data-issue={row.key}
                      aria-current={row.key === issue.key ? 'true' : undefined}
                      onClick={() => onLocation({ issue: row.key, tab: 'issues', focus: null })}
                    >
                      <span>{row.title}</span>
                      <span aria-hidden="true">›</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p>选择问题，查看相关准备内容。</p>
            </aside>
            <div className="scc-issue-main">
              <article className="scc-issue-intro">
                <div>
                  <h2>{issue.title}</h2>
                  <p>{issue.summary}</p>
                  <p className="scc-issue-impact">关注：{issue.question}</p>
                </div>
                <ForestResearchImage file={issue.media} label={issue.title} />
              </article>
              <section className="scc-read-section" ref={information} tabIndex={-1}>
                <div className="scc-heading-row">
                  <h2>需要掌握的信息</h2>
                  <button className="scc-text-button" onClick={toEvidence}>
                    查看相关依据 ↗
                  </button>
                </div>
                <p className="scc-section-lead">以下是需要了解的信息，未表示当前已经取得。</p>
                <ul className="scc-info-rows" data-linked-module="information">
                  {issue.information.map((row) => (
                    <li className="scc-info-row" key={row.name}>
                      <div className="scc-info-row-main">
                        <strong>{row.name}</strong>
                        <p>用途：{row.purpose}</p>
                        {row.detail && (
                          <p className="scc-info-expect">需要获取：{row.detail.what.join('、')}</p>
                        )}
                        <p>通常来源：{row.acquisition}</p>
                      </div>
                      <button
                        className="scc-text-button scc-info-evidence"
                        onClick={(event) => {
                          infoOpener.current = event.currentTarget;
                          setInfoNeed(row);
                        }}
                      >
                        查看依据 ↗
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section" ref={capabilities} tabIndex={-1}>
                <h2>所需能力（研究主题）</h2>
                <p className="scc-section-lead">
                  用于理解可能的实现方式；尚无经批准的正式能力定义关联，不代表本地具备。
                </p>
                <ul className="scc-cap-grid" data-linked-module="capability">
                  {issue.capabilities.map((row) => (
                    <li className="scc-cap-card" key={row.key} data-research-capability={row.key}>
                      <ScenarioCapabilityReadingCard
                        reading={forestCapabilityReading(issue.key, row)}
                        onOpen={(event) => {
                          opener.current = event.currentTarget;
                          setDrawer(row);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section">
                <h2>相关官方依据</h2>
                <ForestSources ids={issue.sourceIds.slice(0, 4)} compact />
                <ForestCases ids={issue.caseIds} />
              </section>
              <footer className="scc-issue-footer">
                <span>所需能力仅为研究方向，尚无正式定义关联。</span>
                <button className="scc-primary" onClick={toEvidence}>
                  查看依据与案例
                </button>
              </footer>
            </div>
          </div>
        ) : (
          <section className="scc-evidence-page">
            <div className="scc-heading-row">
              <h2>依据与案例</h2>
              <div className="scc-evidence-switch" role="group" aria-label="资料范围">
                <button aria-pressed={!all} onClick={() => setAll(false)}>
                  与本问题相关
                </button>
                <button aria-pressed={all} onClick={() => setAll(true)}>
                  本场景全部资料
                </button>
              </div>
            </div>
            <p className="scc-section-lead">
              {all ? '森林场景全部公开资料。' : `与“${issue.title}”相关的资料。`}
              法规、推荐标准目录、公开案例和技术说明分别标识。
            </p>
            <div className="scc-evidence-switch" role="group" aria-label="资料类型">
              {([null, '法规与标准', '实践与事故', '技术参考'] as const).map((value) => (
                <button
                  key={value ?? 'all'}
                  aria-pressed={group === value}
                  onClick={() => setGroup(value)}
                >
                  {value ?? '全部类型'}
                </button>
              ))}
            </div>
            <ForestSources
              ids={
                all ? [...issue.sourceIds, ...forestSources.map((row) => row.id)] : issue.sourceIds
              }
              group={group}
            />
            {(!group || group === '实践与事故') && (
              <ForestCases ids={all ? forestCases.map((row) => row.id) : issue.caseIds} />
            )}
            <ForestExtendedTechnicalIllustrations />
            <p>
              <a href={researchIndex} target="_blank" rel="noopener noreferrer">
                查阅原创中文研究索引 PDF ↗
              </a>{' '}
              · 项目研究整理，非政府发布原文
            </p>
            <button className="scc-text-button" onClick={() => changeTab('issues')}>
              返回当前问题：{issue.title} ↑
            </button>
          </section>
        )}
      </div>
      <p className="scc-notice">
        公开资料整理，供研究与准备；未接入本地预案、资源和现场事实，尚未审定为正式场景包。
      </p>
      {drawer && (
        <ResearchCapabilityDrawer
          capability={drawer}
          issueTitle={issue.title}
          issueKey={issue.key}
          restoreFocusTo={opener.current}
          onClose={() => setDrawer(null)}
        />
      )}
      {infoNeed && (
        <ResearchInformationDrawer
          sceneTitle="森林火灾"
          issueTitle={issue.title}
          needName={infoNeed.name}
          acquisition={infoNeed.acquisition}
          detail={forestInformationDetailOf(infoNeed)}
          sources={forestDrawerSources(forestInformationDetailOf(infoNeed))}
          cases={forestDrawerCases(issue)}
          restoreFocusTo={infoOpener.current}
          onClose={() => setInfoNeed(null)}
        />
      )}
    </section>
  );
}

/**
 * 每一项信息都必须有自己的详解。缺少详解时返回一段明确的说明，而不是借用相邻条目，
 * 让“没有写”和“已经写过”在页面上可以区分。
 */
function forestInformationDetailOf(row: ForestScenarioNeed): ResearchInformationDetail {
  return (
    row.detail ?? {
      why: '这一项尚未编写逐项说明。',
      what: ['待内容 Owner 补充'],
      risk: '说明缺失时，读者无法判断这一项为何需要，也无法判断缺失会造成什么影响。',
      boundary: '在说明补齐之前，不展示推断出的解释。',
      sources: [],
    }
  );
}

type ForestScenarioNeed = ResearchIssue['information'][number];

/** 逐项详解引用的官方资料解析成抽屉里的资料卡；无法解析的编号不会凭空补一张卡。 */
export function forestDrawerSources(
  detail: ResearchInformationDetail,
): readonly ResearchDrawerSource[] {
  return detail.sources.flatMap((ref) => {
    const source = forestSources.find((row) => row.id === ref.id);
    if (!source) return [];
    return [
      {
        id: source.id,
        title: source.title,
        meta: source.kind + ' · ' + source.publisher,
        summary: source.summary,
        relation: ref.relation,
        url: source.url,
        ...(source.documentUrl
          ? {
              documentUrl: source.documentUrl,
              documentLabel: source.documentLabel ?? '查看官方 PDF 原文',
            }
          : {}),
      },
    ];
  });
}

/** 本问题的官方案例，连同它们的资料性质一起带进抽屉。 */
export function forestDrawerCases(issue: ResearchIssue): readonly ResearchDrawerCase[] {
  return issue.caseIds.flatMap((id) => {
    const row = forestCases.find((item) => item.id === id);
    if (!row) return [];
    const source = forestSources.find((item) => item.id === row.sourceId);
    return [
      {
        id: row.id,
        title: row.title,
        meta: row.kindLabel + ' · ' + row.occurredAt + ' · ' + row.location,
        summary: row.summary,
        url: source?.url ?? null,
      },
    ];
  });
}

export function ForestSources({
  ids,
  compact = false,
  group = null,
}: {
  readonly ids: readonly string[];
  readonly compact?: boolean;
  readonly group?: ForestSourceGroup | null;
}) {
  const rows = [...new Set(ids)].flatMap((id) => {
    const source = forestSources.find((row) => row.id === id);
    return source && (!group || source.group === group) ? [source] : [];
  });
  return (
    <div className="scc-evidence-grid">
      {rows.length === 0 && <p>此范围暂无该类型资料，可切换其他类型或查看本场景全部资料。</p>}
      {rows.map((row) => {
        const body = (
          <>
            <p>{row.summary}</p>
            <a href={row.url} target="_blank" rel="noopener noreferrer">
              {row.id === 'CN-FORESTRY-NEWS-VIDEO' ? '查看官方图文与视频入口' : '阅读官方原文'} ↗
            </a>
            {row.documentUrl && (
              <p>
                <a href={row.documentUrl} target="_blank" rel="noopener noreferrer">
                  {row.documentLabel} ↗
                </a>
              </p>
            )}
            {row.id === 'CN-FORESTRY-NEWS-VIDEO' && <p>目标网络播放尚未验证。</p>}
          </>
        );
        return (
          <article className="scc-evidence-card" data-evidence-id={row.id} key={row.id}>
            <span className="scc-chip">
              {row.kind} · {row.publisher}
            </span>
            {compact ? (
              <details>
                <summary>{row.title}</summary>
                {body}
              </details>
            ) : (
              <>
                <h3>{row.title}</h3>
                {body}
              </>
            )}
          </article>
        );
      })}
    </div>
  );
}

function ForestCases({ ids }: { readonly ids: readonly string[] }) {
  if (!ids.length) return null;
  return (
    <section className="scc-forest-cases" aria-label="相关案例与实践">
      <h3>相关案例与实践</h3>
      <div className="scc-evidence-grid">
        {forestCases
          .filter((row) => ids.includes(row.id))
          .map((row) => {
            const source = forestSources.find((source) => source.id === row.sourceId);
            return (
              <article className="scc-evidence-card" data-case-id={row.id} key={row.id}>
                <span className="scc-chip">{row.kindLabel}</span>
                <h3>{row.title}</h3>
                <p>
                  {row.occurredAt} · {row.location}
                </p>
                <p>{row.summary}</p>
                <p>{source?.publisher}</p>
                {source && (
                  <a href={source.url} target="_blank" rel="noopener noreferrer">
                    查看官方案例原文 ↗
                  </a>
                )}
              </article>
            );
          })}
      </div>
    </section>
  );
}

function ResearchCapabilityDrawer({
  capability,
  issueTitle,
  issueKey,
  restoreFocusTo,
  onClose,
}: {
  readonly capability: ResearchCapability;
  readonly issueTitle: string;
  readonly issueKey: string;
  readonly restoreFocusTo: HTMLElement | null;
  readonly onClose: () => void;
}) {
  return (
    <ScenarioCapabilityReadingDrawer
      reading={forestCapabilityReading(issueKey, capability)}
      eyebrow={`研究说明 · 森林火灾 · ${issueTitle}`}
      restoreFocusTo={restoreFocusTo}
      onClose={onClose}
    >
      <details>
        <summary>原问题引用的研究依据</summary>
        <ForestSources ids={capability.sourceIds} />
      </details>
      <p className="scc-drawer-footnote" data-definition-status="NO_APPROVED_MAPPING">
        能力定义暂不可查看：该研究主题尚无专业 Owner 批准的正式能力 ID 映射。
      </p>
      <a href={platformRoutes.equipment}>进入能力中心浏览／查找 →</a>
      <p className="scc-drawer-footnote">通用浏览入口，尚未精确匹配能力定义。</p>
    </ScenarioCapabilityReadingDrawer>
  );
}
